// GLOBÁLIS ZOD-HIBATÉRKÉP — a lokalizált fallback minden űrlapra.
//
// MIÉRT KELL: az űrlapok a kontraktus (vagy a saját) Zod-sémáját használják resolverként, és ahol egy szabálynak
// nincs saját üzenete, ott a Zod ANGOL alapszövege jelent meg a magyar felületen („String must contain at least
// 1 character(s)" — mérve a jegy-létrehozón). A saját `errorMap`-pel rendelkező űrlapok ezt felülírják (a Zod 3
// sorrendje: a séma / a parse-hívás térképe nyer a globális felett); ami nálunk kimarad, ide esik.
//
// A fordítás a hiba PILLANATÁBAN fut (`t` a hívás idején), így a nyelvváltás is azonnal hat.
import { type TFunction } from 'i18next';
import { z } from 'zod';

/** A fallback-térkép (tiszta függvény, tesztelhető): a Zod issue-kód → a `validation:fallback.*` üzenet. */
export function buildFallbackZodErrorMap(t: TFunction): z.ZodErrorMap {
  const msg = (key: string, count?: number | bigint): { message: string } => ({
    message: t(`validation:fallback.${key}`, count === undefined ? {} : { count: Number(count) }),
  });
  return (issue) => {
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        // hiányzó érték (üres mező) → „kötelező"; egyéb típus-eltérés → általános hiba
        return issue.received === 'undefined' || issue.received === 'null'
          ? msg('required')
          : msg('invalid');
      case z.ZodIssueCode.too_small:
        if (issue.type === 'string') {
          return Number(issue.minimum) <= 1 ? msg('required') : msg('tooShort', issue.minimum);
        }
        if (issue.type === 'array' || issue.type === 'set')
          return msg('tooFewItems', issue.minimum);
        if (issue.type === 'number' || issue.type === 'bigint')
          return msg('tooSmall', issue.minimum);
        return msg('invalid');
      case z.ZodIssueCode.too_big:
        if (issue.type === 'string') return msg('tooLong', issue.maximum);
        if (issue.type === 'array' || issue.type === 'set')
          return msg('tooManyItems', issue.maximum);
        if (issue.type === 'number' || issue.type === 'bigint') return msg('tooBig', issue.maximum);
        return msg('invalid');
      case z.ZodIssueCode.invalid_string:
        if (issue.validation === 'email') return msg('email');
        if (issue.validation === 'url') return msg('url');
        return msg('invalid');
      case z.ZodIssueCode.invalid_enum_value:
        return msg('option');
      case z.ZodIssueCode.invalid_date:
        return msg('date');
      default:
        return msg('invalid');
    }
  };
}

/** Telepítés — EGYSZER, az i18n inicializálásakor (`i18n/index.ts`). */
export function installFallbackZodErrorMap(t: TFunction): void {
  z.setErrorMap(buildFallbackZodErrorMap(t));
}
